import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/context/LanguageContext';

const FlagIT = () => (
  <svg viewBox="0 0 640 480" className="w-6 h-4 rounded-sm shadow-sm">
    <g fillRule="evenodd" strokeWidth="1pt">
      <rect fill="#fff" width="640" height="480"/>
      <rect fill="#009246" width="213.3" height="480"/>
      <rect fill="#ce2b37" x="426.7" width="213.3" height="480"/>
    </g>
  </svg>
);

const FlagEN = () => (
  <svg viewBox="0 0 640 480" className="w-6 h-4 rounded-sm shadow-sm">
    <path fill="#012169" d="M0 0h640v480H0z"/>
    <path fill="#FFF" d="m75 0 244 181L562 0h78v62L400 241l240 178v61h-80L320 301 81 480H0v-60l239-178L0 64V0h75z"/>
    <path fill="#C8102E" d="m424 281 216 159v40L369 281h55zm-184 20 6 35L54 480H0l240-179zM640 0v3L391 191l2-44L590 0h50zM0 0l239 176h-60L0 42V0z"/>
    <path fill="#FFF" d="M241 0v480h160V0H241zM0 160v160h640V160H0z"/>
    <path fill="#C8102E" d="M0 193v96h640v-96H0zM273 0v480h96V0h-96z"/>
  </svg>
);

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const { lang, setLang, t } = useLanguage();

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/');
  };

  return (
    <nav className="fixed top-0 left-0 right-0 bg-white/90 backdrop-blur-xl border-b border-gray-200 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex flex-col items-start py-1 relative" data-testid="nav-logo">
            <img 
              src="/logo-martorana.png" 
              alt="Officina Martorana Logo" 
              className="h-10 md:h-14 w-auto max-w-[180px] md:max-w-[300px] object-contain transform hover:scale-105 transition-transform"
              style={{ filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.15))' }}
            />
            <span className="self-end text-[7px] md:text-[9px] font-bold tracking-[0.15em] uppercase -mt-1"
              style={{ background: 'linear-gradient(90deg, #1a3c8f, #c41e3a)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Bosch Car Service
            </span>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-8">
            {/* Language Flags */}
            <div className="flex items-center gap-1.5 ml-4 mr-2" data-testid="lang-switcher">
              <button
                onClick={() => setLang('it')}
                className={`p-1 rounded transition-all ${lang === 'it' ? 'ring-2 ring-[#0066b2] scale-110' : 'opacity-60 hover:opacity-100'}`}
                data-testid="lang-it"
                aria-label="Italiano"
              >
                <FlagIT />
              </button>
              <button
                onClick={() => setLang('en')}
                className={`p-1 rounded transition-all ${lang === 'en' ? 'ring-2 ring-[#0066b2] scale-110' : 'opacity-60 hover:opacity-100'}`}
                data-testid="lang-en"
                aria-label="English"
              >
                <FlagEN />
              </button>
            </div>

            <Link to="/" className="text-gray-700 hover:text-[#0066b2] font-medium transition-colors" data-testid="nav-home">{t('nav', 'home')}</Link>
            <Link to="/servizi" className="text-gray-700 hover:text-[#0066b2] font-medium transition-colors" data-testid="nav-services">{t('nav', 'services')}</Link>
            <Link to="/prenota" className="text-gray-700 hover:text-[#0066b2] font-medium transition-colors" data-testid="nav-booking">{t('nav', 'booking')}</Link>
            <Link to="/blog" className="text-gray-700 hover:text-[#0066b2] font-medium transition-colors" data-testid="nav-blog">{t('nav', 'tips')}</Link>
            <Link to="/eventi" className="text-gray-700 hover:text-[#0066b2] font-medium transition-colors" data-testid="nav-events">{lang === 'en' ? 'Events' : 'Eventi'}</Link>
            <Link to="/contatti" className="text-gray-700 hover:text-[#0066b2] font-medium transition-colors" data-testid="nav-contact">{t('nav', 'contact')}</Link>
            {token ? (
              <>
                <Link to="/dashboard">
                  <Button variant="outline" size="sm" data-testid="nav-dashboard">{t('nav', 'dashboard')}</Button>
                </Link>
                <Button onClick={handleLogout} variant="ghost" size="sm" data-testid="nav-logout">{t('nav', 'logout')}</Button>
              </>
            ) : (
              <Link to="/login">
                <Button variant="outline" size="sm" data-testid="nav-login">{t('nav', 'login')}</Button>
              </Link>
            )}
            <a href="tel:+390925259520" className="flex items-center space-x-2 bg-[#0066b2] text-white px-4 py-2 rounded-lg hover:bg-[#005494] transition-colors" data-testid="nav-phone-button">
              <Phone className="w-4 h-4" />
              <span className="font-medium">{t('nav', 'callNow')}</span>
            </a>
          </div>

          {/* Mobile menu button */}
          <div className="flex items-center gap-3 md:hidden">
            {/* Mobile Language Flags */}
            <div className="flex items-center gap-1" data-testid="lang-switcher-mobile">
              <button
                onClick={() => setLang('it')}
                className={`p-0.5 rounded ${lang === 'it' ? 'ring-2 ring-[#0066b2]' : 'opacity-50'}`}
                aria-label="Italiano"
              >
                <FlagIT />
              </button>
              <button
                onClick={() => setLang('en')}
                className={`p-0.5 rounded ${lang === 'en' ? 'ring-2 ring-[#0066b2]' : 'opacity-50'}`}
                aria-label="English"
              >
                <FlagEN />
              </button>
            </div>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-700 hover:text-[#0066b2] transition-colors"
              data-testid="nav-mobile-toggle"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden bg-white border-t border-gray-200" data-testid="nav-mobile-menu">
          <div className="px-4 py-6 space-y-4">
            <Link to="/" className="block text-gray-700 hover:text-[#0066b2] font-medium py-2" onClick={() => setIsOpen(false)} data-testid="nav-mobile-home">{t('nav', 'home')}</Link>
            <Link to="/servizi" className="block text-gray-700 hover:text-[#0066b2] font-medium py-2" onClick={() => setIsOpen(false)} data-testid="nav-mobile-services">{t('nav', 'services')}</Link>
            <Link to="/prenota" className="block text-gray-700 hover:text-[#0066b2] font-medium py-2" onClick={() => setIsOpen(false)} data-testid="nav-mobile-booking">{t('nav', 'booking')}</Link>
            <Link to="/blog" className="block text-gray-700 hover:text-[#0066b2] font-medium py-2" onClick={() => setIsOpen(false)} data-testid="nav-mobile-blog">{t('nav', 'tips')}</Link>
            <Link to="/eventi" className="block text-gray-700 hover:text-[#0066b2] font-medium py-2" onClick={() => setIsOpen(false)} data-testid="nav-mobile-events">{lang === 'en' ? 'Events' : 'Eventi'}</Link>
            <Link to="/contatti" className="block text-gray-700 hover:text-[#0066b2] font-medium py-2" onClick={() => setIsOpen(false)} data-testid="nav-mobile-contact">{t('nav', 'contact')}</Link>
            {token ? (
              <>
                <Link to="/dashboard" onClick={() => setIsOpen(false)}>
                  <Button className="w-full" variant="outline" data-testid="nav-mobile-dashboard">{t('nav', 'dashboard')}</Button>
                </Link>
                <Button onClick={() => { handleLogout(); setIsOpen(false); }} className="w-full" variant="ghost" data-testid="nav-mobile-logout">{t('nav', 'logout')}</Button>
              </>
            ) : (
              <Link to="/login" onClick={() => setIsOpen(false)}>
                <Button className="w-full" variant="outline" data-testid="nav-mobile-login">{t('nav', 'login')}</Button>
              </Link>
            )}
            <a href="tel:+390925259520" className="flex items-center justify-center space-x-2 bg-[#0066b2] text-white px-4 py-3 rounded-lg hover:bg-[#005494] transition-colors" data-testid="nav-mobile-phone-button">
              <Phone className="w-4 h-4" />
              <span className="font-medium">{t('nav', 'callNow')}</span>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}
